
import { useState } from 'react';

const WeeklyGoals = () => {
  const [goals, setGoals] = useState([
    { id: 1, text: 'Complete 3 grammar lessons', completed: true },
    { id: 2, text: 'Practice pronunciation for 30 minutes', completed: true },
    { id: 3, text: 'Learn 20 new vocabulary words', completed: false },
    { id: 4, text: 'Complete 2 listening exercises', completed: false },
    { id: 5, text: 'Study for 5 consecutive days', completed: false }
  ]);

  const [newGoal, setNewGoal] = useState('');
  const [showAddGoal, setShowAddGoal] = useState(false);

  const toggleGoal = (id: number) => {
    setGoals(goals.map(goal => 
      goal.id === id ? { ...goal, completed: !goal.completed } : goal
    ));
  };

  const addGoal = () => {
    if (newGoal.trim()) {
      const newId = Math.max(...goals.map(g => g.id)) + 1;
      setGoals([...goals, { id: newId, text: newGoal.trim(), completed: false }]);
      setNewGoal('');
      setShowAddGoal(false);
    }
  };

  const removeGoal = (id: number) => {
    setGoals(goals.filter(goal => goal.id !== id));
  };

  const completedGoals = goals.filter(goal => goal.completed).length;
  const completionPercentage = goals.length > 0 ? (completedGoals / goals.length) * 100 : 0;

  return (
    <section id="weekly-goals" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Weekly Learning Goals
            </h2>
            <p className="text-xl text-gray-600">
              Set and track your weekly goals to stay motivated and consistent
            </p>
          </div>

          {/* Progress Overview */}
          <div className="bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl p-8 text-white mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">This Week's Progress</h3>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-4xl font-bold">{completedGoals}</div>
                  <div className="text-green-100">
                    <div>of {goals.length} goals</div>
                    <div>completed</div>
                  </div>
                </div>
                <div className="w-full bg-white/20 rounded-full h-3 mb-2">
                  <div 
                    className="bg-white h-3 rounded-full transition-all duration-500"
                    style={{ width: `${completionPercentage}%` }}
                  ></div>
                </div>
                <div className="text-green-100 text-sm">
                  {Math.round(completionPercentage)}% complete
                </div>
              </div>
              
              <div className="text-center">
                <div className="relative inline-block">
                  <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="35"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="10"
                      fill="none"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="35"
                      stroke="white"
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${completionPercentage * 2.2} 220`}
                      strokeLinecap="round"
                      className="transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-xl font-bold">{Math.round(completionPercentage)}%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Goals List */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Your Goals</h3>
              <button
                onClick={() => setShowAddGoal(!showAddGoal)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line mr-2"></i>
                Add Goal
              </button>
            </div>

            {/* Add New Goal */}
            {showAddGoal && (
              <div className="bg-white rounded-lg p-4 mb-6 border-2 border-blue-200">
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    placeholder="Enter your new goal..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && addGoal()}
                  />
                  <button
                    onClick={addGoal}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-check-line"></i>
                  </button>
                  <button
                    onClick={() => {
                      setShowAddGoal(false);
                      setNewGoal('');
                    }}
                    className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Goals List */}
            <div className="space-y-3">
              {goals.map((goal) => (
                <div 
                  key={goal.id}
                  className={`flex items-center space-x-4 p-4 rounded-lg transition-all ${
                    goal.completed ? 'bg-green-50 border border-green-200' : 'bg-white border border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => toggleGoal(goal.id)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer ${
                      goal.completed 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : 'border-gray-300 hover:border-green-400'
                    }`}
                  >
                    {goal.completed && <i className="ri-check-line text-sm"></i>}
                  </button>
                  
                  <span className={`flex-1 ${
                    goal.completed ? 'text-green-700 line-through' : 'text-gray-900'
                  }`}>
                    {goal.text}
                  </span>
                  
                  <button
                    onClick={() => removeGoal(goal.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>

            {goals.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-target-line text-4xl mb-4 block"></i>
                <p>No goals set yet. Add your first goal to get started!</p>
              </div>
            )}
          </div>

          {/* Motivational Message */}
          {completedGoals > 0 && (
            <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
              <i className="ri-trophy-line text-yellow-600 text-3xl mb-3 block"></i>
              <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                Great job! 🎉
              </h4>
              <p className="text-yellow-700">
                You've completed {completedGoals} goal{completedGoals !== 1 ? 's' : ''} this week. 
                Keep up the excellent work!
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WeeklyGoals;
